import { useState, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { createQueryClient } from '@/lib/query/client';
import { AuthProvider } from '@/lib/auth/AuthProvider';
import { ToastProvider } from '@/lib/ui';
import { TooltipProvider } from '@/lib/ui/Tooltip';
import { ChromeDirectionProvider } from '@/lib/i18n/ChromeDirectionProvider';
import i18n from '@/lib/i18n/config';

/**
 * Single composition root for all app-wide context (plan §3).
 *
 * Context only. Nothing that RENDERS UI of its own belongs here: everything in this file
 * sits ABOVE `<BrowserRouter>`, so a child mounted here has no router context to read.
 * `MediaPickerHost` lives in `App`, inside the router, for exactly that reason.
 */
export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);
  return (
    <I18nextProvider i18n={i18n}>
      <ChromeDirectionProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <TooltipProvider>
              <AuthProvider>{children}</AuthProvider>
            </TooltipProvider>
          </ToastProvider>
        </QueryClientProvider>
      </ChromeDirectionProvider>
    </I18nextProvider>
  );
}
